import $ from "../../libs/jquery-3.3.1.min.js";
import {CustomEase, CustomWiggle, gsap} from "../../libs/gsap/all.js";
import * as PIXI from "pixi.js";
import 'pixi.js/math-extras'
import {BaseMiniGame} from "./BaseMiniGame";
import {GameG_Dropzone} from "./components/GameG_Dropzone";
import {AppearingInteractiveButton} from "./components/AppearingInteractiveButton";

// Game description : Match - Slide the photos in front of the corresponding object
export class GameG extends BaseMiniGame {

  // Constructor
  constructor(data) {
    super(data);
    this.appWidth = this.data.appDimensions.width;
    this.appHeight = this.data.appDimensions.height;
    this.matchCount = this.data.tagsTarget.length;

    this.interactiveZoneSize = {width: 0, height: 0};

    this.dragTarget = false;
    this.tagsObjects = [];
    this.dropzoneObjects = [];
    this.isCheckingAnswer = false;
    this.droppedCount = 0;

    this.$screenEl = null;
    this.beginningTimeline = null;
  }

  async assetLoader() {
    await super.assetLoader();

    if (this.data.medias && this.data.medias.length) {
      for (let i = 0; i < this.data.medias.length; i++) {
        this.assets[this.data.medias[i].name] = await PIXI.Assets.load(this.data.medias[i].url);
      }
    }
  }

  async buildStage() {

    this.$screenEl = $(this.canvas).parents(".screen");

    // Enable events on the whole stage
    this.stage.hitArea = this.screen;

    this.questionTextContainer = this.$screenEl.find(".game-question");
    this.questionTextContainer.css({
      top: this.data.question.y,
      left: this.data.question.x,
    });
    const questionTextHeight = Math.max(this.$screenEl.find(".game-question .en").outerHeight(), this.$screenEl.find(".game-question .fr").outerHeight());

    // Prepare match target
    this.matchContainer = new PIXI.Container();
    this.stage.addChild(this.matchContainer);
    this.matchContainer.position.x = 90;
    this.matchContainer.position.y = 70;

    for (const [matchIndex, match] of this.data.tagsTarget.entries()) {
      const dropzoneObj = new GameG_Dropzone({
        x: (matchIndex % 2) * (520 + 30),
        y: matchIndex >= 2 ? 386 + 30 : 0
      }, match, this);

      const matchZone = new PIXI.Container();
      matchZone.addChild(dropzoneObj);

      this.dropzoneObjects.push(dropzoneObj);
      this.matchContainer.addChild(matchZone);
    }

    // Update interactive zone dimension
    this.interactiveZoneSize.width = this.matchContainer.width + this.matchContainer.position.x;
    this.interactiveZoneSize.height = this.matchContainer.height + this.matchContainer.position.y;

    // Prepare match origin
    this.interactiveContainer = new PIXI.Container();
    this.interactiveContainer.position.x = this.interactiveZoneSize.width + 80;
    this.interactiveContainer.position.y = this.data.question.y + questionTextHeight + 70;

    this.stage.addChild(this.interactiveContainer);

    for (const [matchIndex, match] of this.data.tagsOrigin.entries()) {
      const sourceContainer = new PIXI.Container();
      sourceContainer.cursor = "grab";

      // Tag image
      const sourceBg = new PIXI.Sprite(this.assets[match.image]);
      sourceBg.width = 194;
      sourceBg.height = 184;
      sourceBg.label = "image";

      sourceContainer.addChild(sourceBg);
      sourceContainer.eventMode = "none";
      sourceContainer.position.x = (matchIndex % 2) * (sourceBg.width + 24);
      sourceContainer.position.y = matchIndex >= 2 ? (sourceBg.height + 24) : 0;

      // Tag background
      const tagBg = new PIXI.Sprite(this.assets["match-dropzone"]);
      tagBg.width = sourceBg.width - 10;
      tagBg.height = sourceBg.height - 10;
      tagBg.alpha = 0.0;
      tagBg.label = "sourceBackground";
      tagBg.position.set(sourceContainer.position.x + 5, sourceContainer.position.y + 5);
      this.interactiveContainer.addChild(tagBg);

      // copy original position to a variable for later
      sourceContainer.originalPosition = new PIXI.Point().copyFrom(sourceContainer.position);
      this.interactiveContainer.addChild(sourceContainer);
      sourceContainer.originalGlobalPosition = new PIXI.Point().copyFrom(sourceContainer.getGlobalPosition());
      sourceContainer.originalSize = {width: sourceBg.width, height: sourceBg.height};

      // keep references for later
      sourceContainer.id = match.id;
      sourceContainer.alpha = 0.0;

      // Good/Bad marker
      let markerSize = 42;
      let markerPosition = {
        x: 216 - (markerSize / 2), // 216 is hardcoded GameG_Dropzone dropZone width
        y: 0 - (markerSize / 2)
      };

      sourceContainer.goodAnswerIcon = new PIXI.Sprite(this.assets["good-answer"]);
      sourceContainer.goodAnswerIcon.width = markerSize;
      sourceContainer.goodAnswerIcon.height = markerSize;
      sourceContainer.goodAnswerIcon.position.x = markerPosition.x;
      sourceContainer.goodAnswerIcon.position.y = markerPosition.y;
      sourceContainer.goodAnswerIcon.alpha = 0;
      sourceContainer.addChild(sourceContainer.goodAnswerIcon);

      sourceContainer.badAnswerIcon = new PIXI.Sprite(this.assets["bad-answer"]);
      sourceContainer.badAnswerIcon.width = markerSize;
      sourceContainer.badAnswerIcon.height = markerSize;
      sourceContainer.badAnswerIcon.position.x = markerPosition.x;
      sourceContainer.badAnswerIcon.position.y = markerPosition.y;
      sourceContainer.badAnswerIcon.alpha = 0;
      sourceContainer.addChild(sourceContainer.badAnswerIcon);

      this.tagsObjects.push(sourceContainer);

      /*
       * Events
      */

      // Drag match
      sourceContainer.on("pointerdown", (e) => {
        // save the target so we can keep "this" scope
        this.dragTarget = e.currentTarget;
        this.handleOnDragStart(e);
      }).on("pointerup", (e) => {
        this.handleOnDragEnd(e);
      }).on("pointerupoutside", (e) => {
        this.handleOnDragEnd(e);
      });
    }

    // Add validation button
    let buttonsYPosition = this.interactiveContainer.height + 75 / 2 + 50;
    const checkmark = new PIXI.Sprite(this.assets["checkmark"]);
    const checkmarkDisabled = new PIXI.Sprite(this.assets["checkmark-disabled"]);
    this.validateButton = new AppearingInteractiveButton(75, 75, checkmark, checkmarkDisabled, this.validateAnswers.bind(this), false);
    this.validateButton._zIndex = -1;
    this.validateButton.position.x = this.interactiveContainer.width / 2 + 49;
    this.validateButton.position.y = buttonsYPosition;
    this.interactiveContainer.addChild(this.validateButton);

    // Add reset button
    const reset = new PIXI.Sprite(this.assets["reset"]);
    const resetDisabled = new PIXI.Sprite(this.assets["reset-disabled"]);
    this.resetButton = new AppearingInteractiveButton(75, 75, reset, resetDisabled, this.resetAnswers.bind(this), false);
    this.resetButton._zIndex = -1;
    this.resetButton.position.x = this.interactiveContainer.width / 2 - 46;
    this.resetButton.position.y = buttonsYPosition;
    this.interactiveContainer.addChild(this.resetButton);

    // Used for debugging in pixijs devtool
    //globalThis.__PIXI_APP__ = this;
  }

  handleOnDragStart(e) {
    this.dragTarget.alpha = 0.75;
    const clickPos = this.dragTarget.parent.toLocal(e.global, null);

    // Close info popup if opened
    for (let i = 0; i < this.dropzoneObjects.length; i++) {
      if (this.dropzoneObjects[i].descriptionShown) {
        this.dropzoneObjects[i].closeDescription();
      }
    }

    // place tag on mouse
    gsap.to(this.dragTarget.position, {
      x: clickPos.x - this.dragTarget.width / 2,
      y: clickPos.y - this.dragTarget.height / 2,
      duration: 0.05
    })

    this.stage.eventMode = 'static';
    this.stage.on("pointermove", (e) => {
      this.handleOnDrag(e);
    });
  }

  handleOnDrag(e) {
    if (this.dragTarget) {
      const clickPos = this.dragTarget.parent.toLocal(e.global, null);

      // tag follow mouse
      gsap.killTweensOf(this.dragTarget.position);
      gsap.to(this.dragTarget.position, {
        x: clickPos.x - this.dragTarget.width / 2,
        y: clickPos.y - this.dragTarget.height / 2,
        duration: 0.05
      });
    }
  }

  handleOnDragEnd(e) {
    if (this.dragTarget) {
      this.stage.eventMode = 'auto';
      this.stage.off("pointermove");

      // Reset tag
      this.dragTarget.alpha = 1;

      // Check if we landed on the related dropzone or not
      let droppedInZone = this.checkDropZone(e.global);

      if (droppedInZone !== false) {
        this.droppedCount++;

        droppedInZone.isFilled = true;
        droppedInZone.droppedInZone = this.dragTarget;
        this.dragTarget.droppedInZone = droppedInZone;
        // Disable drag on element
        this.dragTarget.eventMode = "none";
        droppedInZone.animateDropped();

        // move the tag to the dropzone position
        gsap.killTweensOf(this.dragTarget.position);
        gsap.to(this.dragTarget.position, {
          x: -(this.dragTarget.originalGlobalPosition.x - droppedInZone.getBoundingBox().x - this.dragTarget.originalPosition.x),
          y: -(this.dragTarget.originalGlobalPosition.y - droppedInZone.getBoundingBox().y - this.dragTarget.originalPosition.y),
          duration: 0.25
        });

        gsap.to(this.dragTarget.getChildByLabel("image"), {
          width: droppedInZone.dropZone.width,
          height: droppedInZone.dropZone.height,
          duration: 0.25
        });

        if (this.droppedCount > 0) {
          this.resetButton.enable();
        }

        // we went through all the questions, go to end
        if (this.droppedCount >= this.matchCount) {
          this.validateButton.enable();
          return;
        }
      } else {
        // reset position
        gsap.killTweensOf(this.dragTarget.position);
        gsap.to(this.dragTarget.position, {
          x: this.dragTarget.originalPosition.x,
          y: this.dragTarget.originalPosition.y,
          duration: 0.25
        });
      }

      this.dragTarget = false;
    }
  }

  checkDropZone(cursorPos) {

    // check if we dropped in a zone
    for (let i = 0; i < this.dropzoneObjects.length; i++) {
      if (this.dropzoneObjects[i].isFilled || this.dropzoneObjects[i].descriptionShown) {
        continue;
      }
      // get the global position of the drop target so we can compare with the cursor position
      let dropZoneBoundingBox = this.dropzoneObjects[i].getBoundingBox();

      if (
        (cursorPos.x >= dropZoneBoundingBox.left) &&
        (cursorPos.x <= dropZoneBoundingBox.right) &&
        (cursorPos.y >= dropZoneBoundingBox.top) &&
        (cursorPos.y <= dropZoneBoundingBox.bottom)
      ) {
        return this.dropzoneObjects[i];
      }
    }

    return false;
  }

  validateAnswers() {
    let goodAnswers = 0;

    for (let i = 0; i < this.tagsObjects.length; i++) {
      if (this.tagsObjects[i].id !== this.tagsObjects[i].droppedInZone.id) {

        // Shake animation before placing back the tag
        gsap.to(this.tagsObjects[i], {
          x: this.tagsObjects[i].position.x + 10,
          duration: 0.50,
          ease: CustomWiggle.create("wrongAnswerWiggle", {
            wiggles: 4,
            type: "easeOut"
          }),
        });

        gsap.to(this.tagsObjects[i].badAnswerIcon, {
          pixi: {
            alpha: 1.0,
          },
          duration: 0.25,
          ease: "power1.inOut",
        });

        // Place the tag back
        setTimeout(() => {
          gsap.to(this.tagsObjects[i].badAnswerIcon, {
            pixi: {
              alpha: 0.0,
            },
            duration: 0.25,
            ease: "power1.inOut",
          });

          this.tagsObjects[i].droppedInZone.resetState();
          gsap.to(this.tagsObjects[i].position, {
            x: this.tagsObjects[i].originalPosition.x,
            y: this.tagsObjects[i].originalPosition.y,
            duration: 0.25
          });
          gsap.to(this.tagsObjects[i].getChildByLabel("image"), {
            width: this.tagsObjects[i].originalSize.width,
            height: this.tagsObjects[i].originalSize.height,
            duration: 0.25
          });
          this.tagsObjects[i].eventMode = "static";
        }, 1500);

      } else {
        gsap.to(this.tagsObjects[i].goodAnswerIcon, {
          pixi: {
            alpha: 1.0,
          },
          duration: 0.25,
          ease: "power1.inOut",
        });

        this.tagsObjects[i].isValid = true;
        goodAnswers++;
      }
    }

    if (goodAnswers >= this.tagsObjects.length) {
      // Everything is good! let's go to step 2
      this.validateButton.disable();
      this.resetButton.disable();
      console.log("all valid!");

      setTimeout(() => {
        app.animateOutGame();
      }, 2000);
    } else {
      this.droppedCount = goodAnswers;
      this.validateButton.disable();
      this.resetButton.disable();
    }
  }

  resetAnswers() {
    let goodAnswers = 0;

    for (let i = 0; i < this.tagsObjects.length; i++) {

      if (typeof this.tagsObjects[i].droppedInZone !== "undefined" && !this.tagsObjects[i].isValid) {
        this.tagsObjects[i].droppedInZone.resetState();

        gsap.to(this.tagsObjects[i].position, {
          x: this.tagsObjects[i].originalPosition.x,
          y: this.tagsObjects[i].originalPosition.y,
          duration: 0.25
        });
        gsap.to(this.tagsObjects[i].getChildByLabel("image"), {
          width: this.tagsObjects[i].originalSize.width,
          height: this.tagsObjects[i].originalSize.height,
          duration: 0.25
        });
        this.tagsObjects[i].eventMode = "static";

      } else {
        goodAnswers++;
      }
    }

    this.droppedCount = goodAnswers;

    this.resetButton.disable();
    this.validateButton.disable();
  }

  buildBegginingTimeline() {
    this.beginningTimeline = gsap.timeline({paused: true});

    // Question text
    this.beginningTimeline.set(this.questionTextContainer, {
      alpha: 1.0
    });
    this.beginningTimeline.fromTo(this.$screenEl.find('.game-question'), {
      alpha: 0,
    }, {
      alpha: 1,
      ease: "power1.inOut",
      duration: 0.50,
      immediateRender: true
    });

    // Match target
    for (let i = 0; i < this.dropzoneObjects.length; i++) {
      this.beginningTimeline.fromTo(this.dropzoneObjects[i], {
        alpha: 0.0,
        y: this.dropzoneObjects[i].originalPosition.y + 20
      }, {
        alpha: 1.0,
        y: this.dropzoneObjects[i].originalPosition.y,
        duration: 1.0,
        ease: "power1.inOut",
        onComplete: () => {
          // Enable open button
          this.dropzoneObjects[i].openButton.enableInteraction();
        }
      }, i * 0.2);
    }

    // Match origin
    for (let i = 0; i < this.tagsObjects.length; i++) {
      this.beginningTimeline.fromTo(this.tagsObjects[i], {
        alpha: 0.0,
        y: this.tagsObjects[i].originalPosition.y + 40
      }, {
        alpha: 1.0,
        y: this.tagsObjects[i].originalPosition.y + 0,
        duration: 1.0,
        ease: "power1.inOut",
        onComplete: () => {
          // Allow interaction with element
          this.interactiveContainer.getChildrenByLabel("sourceBackground")[i].alpha = 1.0;
          this.tagsObjects[i].eventMode = "static";
        }
      }, i * 0.2);
    }

    // Validate button
    this.beginningTimeline.add(() => {
      this.resetButton.show(false);
    });

    this.beginningTimeline.add(() => {
      this.validateButton.show(false);
    }, "+=0.1");
  }

  beforeAnimateIn() {
    // rebuild timeline

    if (this.beginningTimeline) {
      this.beginningTimeline.pause();
      // this.conclusionTimeline.progress(0, true);
    } else {
      this.buildBegginingTimeline();
    }
  }

  begin() {
    this.beginningTimeline.play(0);
  }

  reset() {
    //console.log("gameG Reset");

    this.droppedCount = 0;
    this.isCheckingAnswer = false;

    this.resetButton.hide();
    this.validateButton.hide();

    // Reset match
    for (let i = 0; i < this.tagsObjects.length; i++) {
      this.tagsObjects[i].isValid = false;
      this.tagsObjects[i].alpha = 0.0;
      this.interactiveContainer.getChildrenByLabel("sourceBackground")[i].alpha = 0.0;
      this.tagsObjects[i].eventMode = "none";
      this.tagsObjects[i].getChildByName("image").width = this.tagsObjects[i].originalSize.width;
      this.tagsObjects[i].getChildByName("image").height = this.tagsObjects[i].originalSize.height;
      this.tagsObjects[i].position.copyFrom(this.tagsObjects[i].originalPosition);

      gsap.to([this.tagsObjects[i].goodAnswerIcon, this.tagsObjects[i].badAnswerIcon], {
        pixi: {
          alpha: 0.0,
        },
        duration: 0.25,
        ease: "power1.inOut",
      });
    }

    // Reset dropzones
    for (let i = 0; i < this.dropzoneObjects.length; i++) {
      this.dropzoneObjects[i].reset();
    }

    // rebuild timeline
    if (this.beginningTimeline) {
      this.beginningTimeline.kill();
      this.beginningTimeline = null;
    }
    this.buildBegginingTimeline();
  }

}
